// Slack Notification Service
// Client-side service for sending notifications to Slack via Supabase Edge Function

import { supabase } from './supabase';

export type SlackEvent =
  | 'signup'
  | 'lesson_generated'
  | 'subscription_created'
  | 'subscription_cancelled'
  | 'waitlist_joined'
  | 'error';

interface SlackNotificationData {
  userId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  gradeLevel?: string;
  subject?: string;
  lessonTitle?: string;
  lessonGrade?: string;
  lessonSubject?: string;
  standardsCount?: number;
  differentiation?: string;
  duration?: string;
  subscriptionTier?: string;
  amount?: number;
  errorMessage?: string;
  context?: string;
  source?: string;
  [key: string]: any;
}

/**
 * Send a notification to Slack via Supabase Edge Function
 *
 * @param event - Type of event to notify about
 * @param data - Event-specific data
 * @returns Promise that resolves when notification is sent
 */
export async function sendSlackNotification(
  event: SlackEvent,
  data: SlackNotificationData
): Promise<void> {
  try {
    // Get Supabase Functions URL from environment
    const functionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;

    if (!functionsUrl) {
      console.warn('[Slack] Functions URL not configured - notification skipped');
      return;
    }

    // Get current user session for authentication
    const { data: { session } } = await supabase.auth.getSession();

    // Get anon key for Supabase API authentication
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    // Call the slack-notifications edge function
    const response = await fetch(`${functionsUrl}/slack-notifications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': session?.access_token ? `Bearer ${session.access_token}` : `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({
        event,
        data,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.details || `HTTP ${response.status}`);
    }

    console.log(`[Slack] ✓ Notification sent: ${event}`);
  } catch (error) {
    // Don't throw - notifications should never break the app
    console.error('[Slack] Failed to send notification:', error);
  }
}

/**
 * Notify about a new user signup
 */
export async function notifyUserSignup(userData: {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  schoolDistrict?: string;
  schoolName?: string;
  gradeLevel?: string;
  subject?: string;
}): Promise<void> {
  await sendSlackNotification('signup', userData);
}

/**
 * Notify about a new lesson generation
 */
export async function notifyLessonGenerated(lessonData: {
  email: string;
  lessonTitle: string;
  lessonGrade: string;
  lessonSubject: string;
  standardsCount?: number;
  differentiation?: string;
  duration?: string;
}): Promise<void> {
  await sendSlackNotification('lesson_generated', lessonData);
}

/**
 * Notify about a new subscription
 */
export async function notifySubscriptionCreated(subscriptionData: {
  email: string;
  subscriptionTier: string;
  amount?: number;
}): Promise<void> {
  await sendSlackNotification('subscription_created', subscriptionData);
}

/**
 * Notify about a cancelled subscription
 */
export async function notifySubscriptionCancelled(subscriptionData: {
  email: string;
  subscriptionTier?: string;
}): Promise<void> {
  await sendSlackNotification('subscription_cancelled', subscriptionData);
}

/**
 * Notify about an error
 */
export async function notifyError(errorData: {
  errorMessage: string;
  email?: string;
  context?: string;
}): Promise<void> {
  await sendSlackNotification('error', errorData);
}

/**
 * Notify about a waitlist signup
 */
export async function notifyWaitlistJoined(waitlistData: {
  email: string;
  source?: string;
}): Promise<void> {
  await sendSlackNotification('waitlist_joined', waitlistData);
}
